import { useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router';

import { AuthStatus } from '../types';
import {
  clearCompanionRetryAttempted,
  peekCompanionAuthParams,
  peekCompanionRetryAttempted,
  readAndClearCompanionForceLogin,
  setCompanionRetryAttempted,
  stashCompanionAuthParams,
} from '../utils/companionDeeplink';
import { requestCompanionAuthorize } from '../utils/requestCompanionAuthorize';

import { getConfig } from '@/config';
import { APP_ROUTES } from '@/constants/routes';

/**
 * Launcher for the hybrid companion auth flow.
 *
 * 1. Logs the Auth0 SPA in within the system browser (so the browser stays
 *    authenticated — web/details pages opened later from the companion don't
 *    re-prompt — and so the completion page can resolve Teams/Webex join URLs).
 * 2. Once authenticated, calls the backend `/auth/companion/authorize` as an
 *    AUTHENTICATED request (Bearer SPA token — a top-level redirect can't carry
 *    the token) and navigates to the Auth0 authorize URL it returns. The backend
 *    rides the just-established Auth0 SSO session silently to mint the companion's
 *    server-held refresh token and push the handle over WS.
 *
 * The original query is stashed in sessionStorage BEFORE the login redirect (the
 * Auth0 round-trip drops the query) and read back afterwards.
 */
export const useExternalAuth = (): AuthStatus => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } =
    useAuth0();
  const [isError, setIsError] = useState(false);
  // Latch so the authenticated handoff POST fires exactly once across re-renders.
  const handoffStartedRef = useRef(false);

  // Stash params on first arrival (when present), before any login redirect.
  // A fresh launch carries ?sessionId=...; the retry relaunch navigates to
  // /external-auth with no query, so clearing the one-shot retry cap here resets
  // it for a NEW flow only — the retry relaunch preserves it.
  useEffect(() => {
    if (searchParams.get('sessionId')) {
      stashCompanionAuthParams(searchParams.toString());
      clearCompanionRetryAttempted();
    }
  }, [searchParams]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      void loginWithRedirect({
        authorizationParams: {
          prompt: 'login',
          redirect_uri: `${getConfig().AUTH.auth0RedirectUrl}${APP_ROUTES.EXTERNAL_AUTH}`,
        },
        appState: { returnTo: APP_ROUTES.EXTERNAL_AUTH },
        // A rejected login must surface an error, not hang forever on "Loading…"
        // (isLoading is derived as !isError).
      }).catch(() => setIsError(true));
      return;
    }

    if (handoffStartedRef.current) {
      return;
    }

    // Authenticated: hand off to the backend confidential flow. A top-level
    // redirect can't carry the SPA Bearer token, so POST the authorize request
    // (authenticated) and navigate to the Auth0 URL the backend returns; it rides
    // the SSO session silently to mint the companion refresh token and deliver the
    // handle to the embedded companion over WS.
    const params = peekCompanionAuthParams() ?? searchParams;
    const sessionId = params.get('sessionId');
    const platform = params.get('platform');
    if (!sessionId || !platform) {
      setIsError(true);
      return;
    }

    handoffStartedRef.current = true;
    void (async () => {
      try {
        // Read and clear the retry flag BEFORE the async calls so a mid-flight
        // failure cannot leave a stale flag for the next launch attempt.
        const forceLogin = readAndClearCompanionForceLogin();

        const [token, idTokenClaims] = await Promise.all([
          getAccessTokenSilently(),
          getIdTokenClaims(),
        ]);

        // Extract early so TypeScript can narrow without a non-null assertion.
        const idTokenHint = idTokenClaims?.__raw;

        // Use prompt=login when: (a) retrying after a subject mismatch, or
        // (b) id_token unavailable (localstorage cache miss) — a missing hint
        // silently falls back to the unguarded SSO session.
        const promptLogin = forceLogin || !idTokenHint;

        const { authorizeUrl } = await requestCompanionAuthorize(
          {
            sessionId,
            platform,
            ...(promptLogin ? {} : { idTokenHint }),
            ...(promptLogin && { promptLogin: true as const }),
          },
          token,
        );
        window.location.replace(authorizeUrl);
      } catch {
        // Last-resort recovery: the SPA is authenticated but the handoff broke —
        // most often an expired Auth0 SSO session (getAccessTokenSilently throws
        // login_required). One fresh interactive login re-establishes it; the flow
        // re-runs on return and rides it silently. Gate on the one-shot cap being
        // unspent AND the stash being present (skip the doomed prompt for
        // storage-blocked users, mirroring the completion side). We do NOT set
        // force-login: the SPA login IS the interactive prompt; setting it would
        // make the post-login handoff prompt again — a second credential prompt.
        if (!peekCompanionRetryAttempted() && peekCompanionAuthParams() !== null) {
          setCompanionRetryAttempted(); // must precede the redirect (sync storage)
          void loginWithRedirect({
            authorizationParams: {
              prompt: 'login',
              redirect_uri: `${getConfig().AUTH.auth0RedirectUrl}${APP_ROUTES.EXTERNAL_AUTH}`,
            },
            appState: { returnTo: APP_ROUTES.EXTERNAL_AUTH },
          }).catch(() => setIsError(true)); // reject must not hang on "Loading…"
          return;
        }
        setIsError(true);
      }
    })();
  }, [
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
    searchParams,
  ]);

  return {
    isLoading: !isError,
    isSuccess: false,
    isError,
    error: isError ? new Error('Missing sessionId or platform') : undefined,
    // Offer a manual re-login only when it could actually re-run the flow, i.e.
    // the stash survived. Hidden for lost-stash dead-ends where it can't help.
    canManualRetry: isError && peekCompanionAuthParams() !== null,
  };
};
